import { Component, Inject, OnInit, Optional } from '@angular/core';

import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import {
    ChoicePossRuleGet,
    ChoicePossRulesService,
    ChosenProductItem,
    FurnitureFabricItem,
    ModelItem,
    ProductChoiceItem,
    ProductNameItem,
} from '@app/services/choice-poss-rules.service';
import { TypeaheadService } from '@app/services/typeahead.service';

@Component({
    selector: 'tt-choice-poss-rule',
    templateUrl: './choice-poss-rule.component.html',
    styleUrls: ['./choice-poss-rule.component.css'],
    standalone: false,
})
export class ChoicePossRuleComponent implements OnInit {
    public page?: ChoicePossRuleGet;

    public selectListProductChoices: ProductChoiceItem[] = [];

    public selectListModels: ModelItem[] = [];

    public selectListProductNames: ProductNameItem[] = [];

    public selectListFurnitureFabrics: FurnitureFabricItem[] = [];

    public selectListChosenProducts: ChosenProductItem[] = [];

    public saving = false;

    public deleting = false;

    constructor(
        private choicePossRules: ChoicePossRulesService,
        private modal: ModalService,
        private typeahead: TypeaheadService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public searchProducts = (params?: { value?: string }) => {
        return this.typeahead.lookUpProducts({
            prod_name: params?.value ?? '',
            webpage_name: 'choicepossrule',
            parm1: '0',
            parm2: '0',
            supplier_no: '0',
            search_only_this_supplier: '0',
            businessco_no: '0',
            onlyonstock: '0',
            model_id: this.page?.model_id ?? 'ALL',
        });
    };

    public async onModelChanged(value: string): Promise<void> {
        if (!this.page || !value) return;

        this.page.model_id = value;
        this.clearProduct();
        await this.loadModelDetails();
    }

    public async onChoiceProductChanged(value: string): Promise<void> {
        if (!this.page || !value) return;

        this.page.choice_prod_id = value;
        await this.clearFurnitureFabrics();
        await this.loadChoiceProductDetails();
    }

    public async onFurnitureFabricsChanged(value: string): Promise<void> {
        if (!this.page || !value) return;

        this.page.furniturefabrics_id = value;
        if (value === 'ALL') this.clearChosenProduct();

        await this.loadChosenProducts();
    }

    public onProductNameChanged(value: string): void {
        if (!this.page) return;

        this.page.prod_id = value;
        const product = this.selectListProductNames.find((item) => item.prod_id === value);
        this.page.prod_name = product?.prod_name ?? '';
    }

    public onProductSearchSelected(item?: { item_id: string | number; item_name?: string }): void {
        if (!this.page) return;

        this.page.prod_id = `${item?.item_id ?? ''}`;
        this.page.prod_name = item?.item_name ?? '';
    }

    public onProductSearchCleared(): void {
        this.clearProduct();
    }

    public async save(): Promise<void> {
        if (!this.page) return;

        this.saving = true;
        try {
            const response = await this.choicePossRules.saveRule(this.page);
            if (response?.errorcode !== '0') {
                await this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response?.errormessage ?? '' });
                return;
            }

            await this.navigator?.back();
        } finally {
            this.saving = false;
        }
    }

    public async delete(): Promise<void> {
        if (!this.page) return;

        this.deleting = true;
        try {
            const confirmed = await this.modal.openConfirmDialog({
                type: 'danger',
                title: 'confirm_title',
                translateTitle: true,
                message: 'confirm_message',
                translateMessage: true,
                ok: 'confirm_ok_label',
                cancel: 'confirm_cancel_label',
            });

            if (!confirmed) return;

            await this.choicePossRules.deleteRule(this.page);
            await this.navigator?.back();
        } finally {
            this.deleting = false;
        }
    }

    private async init(): Promise<void> {
        const choicePossRuleKeyno = this.navigator?.params['choicepossrule_keyno'];
        if (choicePossRuleKeyno === undefined) return;

        await Promise.all([this.loadProductChoices(), this.loadModels(), this.loadPage(choicePossRuleKeyno)]);
        await Promise.all([this.loadModelDetails(), this.loadChoiceProductDetails()]);
    }

    private async loadPage(choicepossruleKeyno: unknown): Promise<void> {
        this.page = await this.choicePossRules.getRule({ choicepossrule_keyno: `${choicepossruleKeyno}` });
    }

    private async loadProductChoices(): Promise<void> {
        this.selectListProductChoices = await this.choicePossRules.listProductChoices();
    }

    private async loadModels(): Promise<void> {
        this.selectListModels = await this.choicePossRules.listModels({ add_all: '1', show_inactive: '0' });
    }

    private async loadModelDetails(): Promise<void> {
        if (!this.page?.model_id?.trim()) return;

        const response = await this.choicePossRules.changeModelId(this.page);
        this.page.nbr_of_products = response.nbr_of_products;
        await this.loadProductNames();
    }

    private async loadChoiceProductDetails(): Promise<void> {
        if (!this.page?.choice_prod_id?.trim()) return;

        const response = await this.choicePossRules.changeChoiceProductId({ choice_prod_id: this.page.choice_prod_id });
        this.page.nbr_of_furniturefabrics = response.nbr_of_furniturefabrics;
        await Promise.all([this.loadChosenProducts(), this.loadFurnitureFabrics()]);
    }

    private async loadProductNames(): Promise<void> {
        if (!this.page?.model_id?.trim() || this.page.nbr_of_products >= 200) return;

        this.selectListProductNames = await this.choicePossRules.listProductNames(this.page);
    }

    private async loadFurnitureFabrics(): Promise<void> {
        if (!this.page?.choice_prod_id?.trim() || this.page.nbr_of_furniturefabrics <= 0) return;

        this.selectListFurnitureFabrics = await this.choicePossRules.listFurnitureFabrics({ add_all: '1', choice_prod_id: this.page.choice_prod_id });
    }

    private async loadChosenProducts(): Promise<void> {
        if (!this.page?.choice_prod_id || !this.page.furniturefabrics_id) return;

        this.selectListChosenProducts = await this.choicePossRules.listChosenProducts(this.page);
    }

    public async goToProduct(prodId?: string): Promise<void> {
        if (!prodId?.trim()) return;

        const product = await this.choicePossRules.getProductKeyno({ prod_id: prodId });
        if (product?.prod_keyno) this.navigator?.go({ to: 'product', parms: { prod_id: product.prod_keyno } });
    }

    private clearProduct(): void {
        if (!this.page) return;

        this.page.prod_id = '';
        this.page.prod_name = '';
    }

    private async clearFurnitureFabrics(): Promise<void> {
        if (!this.page) return;

        this.page.furniturefabrics_id = 'ALL';
        this.clearChosenProduct();
        await this.loadChosenProducts();
    }

    private clearChosenProduct(): void {
        if (this.page) this.page.choosed_prod_id = '';
    }

    ngOnInit(): void {
        void this.init();
    }
}
